import { dateRangeFromISO, getDateRange } from "@app/lib/date-range";
import { getPerspectiveId } from "@app/lib/perspective";
import TransactionsPage, {
  PAGE_SIZE,
  transactionsQuery,
} from "@app/pages/transactions/transactions-page";
import type { TransactionsSearch } from "@app/pages/transactions/transactions-page";
import type { transactionsPage_Query } from "@app/queries/transactionsPage_Query.graphql";
import { environment } from "@app/relay/relay-environment";
import { pageHead } from "@app/lib/page-title";
import { createFileRoute } from "@tanstack/react-router";
import { loadQuery } from "react-relay";

export const Route = createFileRoute("/_authed/transactions_/$month")({
  validateSearch: (search: Record<string, unknown>): TransactionsSearch => ({
    account: typeof search.account === "string" ? search.account : undefined,
    flow:
      search.flow === "COST" ||
      search.flow === "REVENUE" ||
      search.flow === "INTERNAL"
        ? search.flow
        : undefined,
    attributed:
      typeof search.attributed === "boolean" ? search.attributed : undefined,
    categorized:
      typeof search.categorized === "boolean" ? search.categorized : undefined,
    query: typeof search.query === "string" ? search.query : undefined,
    sort:
      search.sort === "DATE" ||
      search.sort === "AMOUNT" ||
      search.sort === "DESCRIPTION" ||
      search.sort === "COUNTERPARTY" ||
      search.sort === "CATEGORY" ||
      search.sort === "ACCOUNT"
        ? search.sort
        : undefined,
    sortDirection:
      search.sortDirection === "ASC" || search.sortDirection === "DESC"
        ? search.sortDirection
        : undefined,
    offset: typeof search.offset === "number" ? search.offset : undefined,
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ params, deps }) =>
    loadQuery<transactionsPage_Query>(
      environment,
      transactionsQuery,
      {
        month: params.month,
        from: dateRangeFromISO(getDateRange()) ?? undefined,
        accountNumber: deps.account,
        flow: deps.flow,
        attributed: deps.attributed,
        categorized: deps.categorized,
        perspectiveId: getPerspectiveId() ?? undefined,
        query: deps.query,
        sort: deps.sort,
        sortDirection: deps.sortDirection,
        dailyHealth: true,
        // The browser hides the needs-review worklist set — a txn sent there leaves
        // this list and is worked on /needs-review (same as the /transactions route).
        excludeNeedsReview: true,
        limit: PAGE_SIZE,
        offset: deps.offset ?? 0,
      },
      { fetchPolicy: "network-only" }
    ),
  head: ({ params }) => pageHead(`Transactions ${params.month}`),
  component: TransactionsMonthRoute,
});

function TransactionsMonthRoute() {
  const queryRef = Route.useLoaderData();
  const search = Route.useSearch();
  const { month } = Route.useParams();
  const navigate = Route.useNavigate();

  // The page owns its suspense/error boundary, drawn below its header — so a
  // month step (or any refetch) never takes the title and arrows off screen.
  return (
    <TransactionsPage
      queryRef={queryRef}
      search={search}
      monthOverride={month}
      // Any filter/sort change resets to page 0; Prev/Next keeps the rest.
      onSearchChange={(next) => navigate({ search: { ...next, offset: 0 } })}
      onPageChange={(offset) => navigate({ search: { ...search, offset } })}
    />
  );
}
